import { useCallback, useMemo } from "react";
import {
  BaseEdge,
  EdgeLabelRenderer,
  EdgeProps,
  getSmoothStepPath,
  getStraightPath,
} from "@xyflow/react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectPlacementIntent,
  selectNodeById,
  selectCopiedNode,
  selectIsEdgeTaken,
} from "../../../../../redux/slices/dynamicTriggerSlice";
import { RootState } from "../../../../../redux/store";
import { useFlowGraphHook } from "../../../hooks/useFlowGraphHook";
import { NODE_TYPE } from "../Nodes/utils/types";
import { canAcceptPlacement, canPasteAtEdge } from "../../../events";
import { getApiErrorMessage } from "../../../util/normalizedHelpers";
import PlusIcon from "../../../../../assets/svg/plus-icon";
import { customToast } from "../../../../../utils/custom-toast/custom-toast";
import { BUILDER_TOAST_CONFIG } from "../../../CreateEditJourney/utils/constants";
import { RULE_NODE_TYPES } from "../Nodes/utils/constants";
import { dynamicTriggerEngineApi } from "../../../../../redux/services/dynamicTriggerEngineApi";

const VERTICAL_ALIGN_THRESHOLD = 5;

const SolidEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  source,
  target,
  style = {},
}: EdgeProps) => {
  const dispatch = useDispatch();
  const journeyId = useSelector(
    (state: RootState) => state.dynamicTrigger.journeyDetails.journeyId,
  );
  const placementIntent = useSelector(selectPlacementIntent);
  const copiedNode = useSelector(selectCopiedNode);
  // The target names the parent it was reached from, so a shortcut between two visited nodes is
  // correctly left plain.
  const isTakenPath = useSelector(selectIsEdgeTaken(source, target));
  const { addNodeAndPersist, pasteCopiedNode } = useFlowGraphHook();
  const sourceNode = useSelector((state: RootState) =>
    selectNodeById(state, source),
  );
  const isVerticallyAligned =
    Math.abs(sourceX - targetX) < VERTICAL_ALIGN_THRESHOLD;

  const [edgePath] = isVerticallyAligned
    ? getStraightPath({ sourceX, sourceY, targetX, targetY })
    : getSmoothStepPath({
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
        borderRadius: 16,
      });

  const midX = (sourceX + targetX) / 2;
  const midY = (sourceY + targetY) / 2;

  const showPlusButton = useMemo(() => {
    if (placementIntent === null || sourceNode == null) return false;
    if (placementIntent.source === "paste") {
      return (
        copiedNode != null && canPasteAtEdge(sourceNode.data, copiedNode.data)
      );
    }
    return canAcceptPlacement(sourceNode.data, placementIntent.nodeType);
  }, [placementIntent, sourceNode, copiedNode]);

  const handleInsertNode = useCallback(
    async (nodeType: NODE_TYPE) => {
      try {
        await addNodeAndPersist({
          node: { nodeType, parentNodeId: source },
          edgeId: id,
        });
      } catch (error) {
        customToast(BUILDER_TOAST_CONFIG.NODE_SAVE_FAILED, "error");
        console.error(error);
      } finally {
        if (nodeType === RULE_NODE_TYPES.CONDITIONAL_SPLIT) {
          //refetch details
          dispatch(
            dynamicTriggerEngineApi.util.invalidateTags([
              { type: "JourneyDetails", id: journeyId },
            ]),
          );
        }
      }
    },
    [addNodeAndPersist, source, id, journeyId, dispatch],
  );

  const handlePasteNode = useCallback(async () => {
    try {
      await pasteCopiedNode({
        parentNodeId: source,
        childNodeId: target,
        edgeId: id,
      });
    } catch (error) {
      customToast(
        getApiErrorMessage(error) ?? BUILDER_TOAST_CONFIG.NODE_PASTE_FAILED,
        "error",
        5000,
      );
      console.error(error);
    }
  }, [pasteCopiedNode, source, target, id]);

  const handlePlusClick = useCallback(() => {
    if (!placementIntent) return;
    if (placementIntent.source === "paste") {
      handlePasteNode();
      return;
    }
    handleInsertNode(placementIntent.nodeType);
  }, [placementIntent, handleInsertNode, handlePasteNode]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const nodeType = e.dataTransfer.getData("application/reactflow-nodetype");
      if (nodeType) {
        handleInsertNode(nodeType as NODE_TYPE);
      }
    },
    [handleInsertNode],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  // Paste mode gets the same drop-target highlight as dragging from the panel.
  const isPlacementHighlight =
    placementIntent?.source === "drag" || placementIntent?.source === "paste";

  return (
    <>
      <BaseEdge
        path={edgePath}
        className="text-schemes dark:text-schemes-dk"
        style={{
          ...style,
          // Blue only for a hop a lead actually travelled, while its path is on screen. Colour is
          // the whole difference — a thicker stroke makes the smooth-step paths read as bent, so
          // the line stays exactly the weight it has everywhere else.
          stroke: isTakenPath ? "var(--color-verba-blue)" : "#898989",
          strokeWidth: 1,
        }}
      />
      {showPlusButton && (
        <EdgeLabelRenderer>
          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className={`rounded-full transition-all duration-200 ${
              isPlacementHighlight
                ? "bg-verba-blue/10 dark:bg-verba-blue-dk/10 border-2 border-dashed border-verba-blue/40 dark:border-verba-blue-dk/40"
                : ""
            }`}
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${midX}px, ${midY}px)`,
              pointerEvents: "all",
              padding: isPlacementHighlight ? "50px" : "12px",
            }}
          >
            <button
              type="button"
              onClick={handlePlusClick}
              className={`px-1.5 py-1 rounded-full flex items-center justify-center shadow-popper-blur-effect transition-all duration-200 text-pure-white dark:text-pure-white-dk bg-help-tag-text-dk dark:bg-help-tag-text hover:bg-help-tag-text-dk/80 dark:hover:bg-help-tag-text/80 border border-help-tag-text-dk dark:border-help-tag-text cursor-pointer ${
                isPlacementHighlight ? "scale-110" : ""
              }`}
            >
            <PlusIcon className="w-2.5 h-2.5 stroke-2  shrink-0" />
          </button>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

export default SolidEdge;
